import { useEffect, useRef, useState, useMemo, memo } from 'react';
import { List as VirtualList, type RowComponentProps } from 'react-window';
import { retryDelayMs, bustCache, tileState } from './tileRetry';

// ─── Shared already-uploaded gallery grid ────────────────────────────────────
// ONE virtualized, iPhone-Photos-style square grid for photos that are ALREADY
// uploaded to the server. Used by:
//   - MagicUploadPage's <UploadedGallery> (review-and-delete between batches)
//   - AlbumFlowPage's <StepUpload> ("X photos déjà importées" strip)
//
// WHY virtualized: the in-flow StepUpload grid used to render EVERY tile with a
// plain `.map` and loaded the 1400px display tier (`signed_url`) into ~56px
// tiles → 100+ heavy <img> nodes on iOS → jank / RAM pressure. This grid mounts
// only on-screen rows (react-window v2) and the caller feeds it the small
// (~320px) `tray_signed_url` tier, so the DOM and bytes stay bounded.
//
// The windowing approach (ResizeObserver-derived column count, numeric pixel
// row height, ~60vh capped scroll viewport) is lifted verbatim from
// MagicUploadPage's in-progress `WindowedTileGrid`.
//
// Generic + API-agnostic: the caller maps its own photo shape into the minimal
// `UploadedTilePhoto` and passes its existing select / delete handlers.

// On-brand colours. These match the exact hexes the original MagicUploadPage
// `UploadedGallery` used (its local BRAND palette: gold #C9A24A, border
// #E5E0D2, creamSoft #FAF6EC) so the live gallery stays pixel-identical after
// the refactor. They line up with `COLORS` in `@/lib/brand`.
const GOLD = '#C9A24A';
const BORDER = '#E5E0D2';
const CREAM_SOFT = '#FAF6EC';

export type UploadedTilePhoto = {
  id: string;
  /** Small (~320px `tray_signed_url`) tier. NOT the 1400px display tier. */
  thumbUrl: string | null;
  selected?: boolean;
  badge?: 'error' | 'done' | null;
  /** No local/server image yet (cold server transform). Render a shimmer
   *  placeholder instead of the static "no image" icon — distinguishes a tile
   *  that's still loading from one that genuinely has no preview. */
  loading?: boolean;
};

type TileRowProps = {
  columns: number;
  count: number;
  gap: number;
  photos: UploadedTilePhoto[];
  selectMode: boolean;
  onToggleSelect?: (id: string) => void;
  onDelete?: (id: string) => void;
};

// ─── Single tile (memoized) ──────────────────────────────────────────────────
// Renders the small thumbUrl as a square, upright `object-cover` image, plus
// the select-circle (select mode) and the ✕ delete chip (when deletable).
const Tile = memo(function Tile({
  photo,
  selectMode,
  onToggleSelect,
  onDelete,
}: {
  photo: UploadedTilePhoto;
  selectMode: boolean;
  onToggleSelect?: (id: string) => void;
  onDelete?: (id: string) => void;
}) {
  const isSel = !!photo.selected;
  const canSelect = selectMode && !!onToggleSelect;

  // FIX #4a — a cold/expired server transform (`thumbUrl`) can fail to load,
  // which used to leave a broken/blank box. Track a per-tile load failure and,
  // on error, fall back to the loading shimmer (never a dead blank tile). Reset
  // when the URL changes (e.g. the server transform finally warms up / re-signs).
  //
  // ⚠️ AND THEN IT SPUN FOR EVER. 2026-08-24: six photos arrived as HEIC, which
  // no browser can draw, and sat behind that shimmer until the conversion sweep
  // caught up three minutes later. Nothing was broken and the album looked
  // broken — reported as data loss, on top of a real data-loss incident. The
  // fallback was honest for about five seconds and a lie after that: it never
  // retried, so it stayed until the page was reloaded, and it never said why.
  //
  // Now it heals itself on a backoff and, once it has actually failed, says so.
  // See ./tileRetry.ts — the rule lives there so it can be tested without a
  // browser, and it is deliberately NOT HEIC-specific: a cold transform, a
  // signed URL that expired a second ago and a conversion that has not run yet
  // are the same thing to a browser and all three heal the same way.
  const [imgFailed, setImgFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => { setImgFailed(false); setAttempt(0); }, [photo.thumbUrl]);

  useEffect(() => {
    if (!imgFailed || !photo.thumbUrl) return undefined;
    const wait = retryDelayMs(attempt + 1);
    if (wait === null) return undefined;              // retries spent — say so
    const t = setTimeout(() => { setAttempt((n) => n + 1); setImgFailed(false); }, wait);
    return () => clearTimeout(t);
  }, [imgFailed, attempt, photo.thumbUrl]);

  const state = tileState({
    hasUrl: !!photo.thumbUrl, failed: imgFailed, attempt, loading: photo.loading,
  });
  const showImg = state === 'image';
  const showShimmer = state === 'loading' || state === 'preparing';

  return (
    <div
      onClick={canSelect ? () => onToggleSelect!(photo.id) : undefined}
      className={`relative rounded-lg overflow-hidden select-none${canSelect ? ' cursor-pointer' : ''}`}
      style={{ outline: isSel ? `3px solid ${GOLD}` : 'none', outlineOffset: '-3px' }}
    >
      {/* Bulletproof 1:1 box */}
      <span aria-hidden className="block" style={{ paddingTop: '100%' }} />
      {showImg ? (
        <img
          // A failed image is cached, so re-setting an identical `src` is a
          // no-op — the retry needs a URL the browser will actually re-request.
          src={bustCache(photo.thumbUrl!, attempt)}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          loading="lazy"
          decoding="async"
          draggable={false}
          // FIX #4a — never a blank box on a failed/cold/expired transform: flip
          // to the shimmer placeholder; the next list refresh re-signs the URL.
          onError={() => setImgFailed(true)}
        />
      ) : showShimmer ? (
        // LOADING placeholder — animated shimmer + spinner. Used while neither a
        // local preview nor the (cold) server transform is ready yet, so the tile
        // is never a dead blank box (FIX #5).
        <div className="absolute inset-0 overflow-hidden" style={{ background: CREAM_SOFT }}>
          <div className="absolute inset-0 tile-shimmer" />
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1">
            <span
              className="w-5 h-5 rounded-full border-2 border-t-transparent animate-spin"
              style={{ borderColor: GOLD, borderTopColor: 'transparent' }}
            />
            {/* ⚠️ SILENT ON THE FIRST LOAD, honest after a real failure. A tile
                that explains itself before it has even failed once is noise on
                a fast connection. */}
            {state === 'preparing' && (
              <span className="text-[9px] leading-none text-center px-1" style={{ color: '#8A7A4F' }}>
                en préparation
              </span>
            )}
          </div>
        </div>
      ) : state === 'unavailable' ? (
        // ⚠️ THE END STATE IS A STATEMENT, NOT MORE SPINNING. The retries are
        // spent; saying "aperçu indisponible" is the difference between a tile
        // somebody reports and a tile somebody understands. The PHOTO is still
        // there — only its preview failed — so this must not read as deleted.
        <div
          className="absolute inset-0 flex flex-col items-center justify-center gap-1 px-1"
          style={{ background: CREAM_SOFT }}
          role="status"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#8A7A4F" strokeWidth="1.6">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M21 15l-5-5L5 21" />
          </svg>
          <span className="text-[9px] leading-tight text-center" style={{ color: '#8A7A4F' }}>
            aperçu indisponible
          </span>
        </div>
      ) : (
        <div className="absolute inset-0 flex items-center justify-center" style={{ background: CREAM_SOFT }}>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="1.6">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <circle cx="8.5" cy="8.5" r="1.5" />
            <path d="M21 15l-5-5L5 21" />
          </svg>
        </div>
      )}

      {/* Dim overlay when selected */}
      {isSel && <div className="absolute inset-0" style={{ background: 'rgba(201,162,74,0.28)' }} />}

      {/* Error badge */}
      {photo.badge === 'error' && (
        <div className="absolute inset-0 bg-black/45 flex items-center justify-center text-white text-lg">⚠️</div>
      )}

      {/* Selection check-circle (top-left) — only in select mode */}
      {canSelect && (
        <div
          className="absolute top-1 left-1 w-5 h-5 rounded-full flex items-center justify-center shadow"
          style={{
            background: isSel ? GOLD : 'rgba(255,255,255,0.85)',
            border: isSel ? 'none' : `1.5px solid ${BORDER}`,
          }}
        >
          {isSel && (
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          )}
        </div>
      )}

      {/* Single-delete × (top-right) — when a delete handler is provided */}
      {onDelete && (
        <button
          type="button"
          aria-label="Supprimer cette photo"
          onClick={(e) => { e.stopPropagation(); onDelete(photo.id); }}
          className="absolute top-1 right-1 w-6 h-6 rounded-full text-white flex items-center justify-center text-sm font-bold shadow-md active:scale-95 transition"
          style={{ background: 'rgba(0,0,0,0.65)' }}
        >
          ×
        </button>
      )}
    </div>
  );
});

// ─── Windowed row (one react-window row = `columns` tiles) ───────────────────
function TileRow({
  index,
  style,
  columns,
  count,
  gap,
  photos,
  selectMode,
  onToggleSelect,
  onDelete,
}: RowComponentProps<TileRowProps>) {
  const start = index * columns;
  const tiles: React.ReactNode[] = [];
  for (let col = 0; col < columns; col++) {
    const i = start + col;
    if (i >= count) break;
    const p = photos[i];
    tiles.push(
      <div key={p.id} style={{ flex: `0 0 calc((100% - ${(columns - 1) * gap}px) / ${columns})`, minWidth: 0 }}>
        <Tile photo={p} selectMode={selectMode} onToggleSelect={onToggleSelect} onDelete={onDelete} />
      </div>,
    );
  }
  return (
    <div style={{ ...style, display: 'flex', gap, paddingBottom: gap, alignItems: 'flex-start' }}>
      {tiles}
    </div>
  );
}

export function UploadedTileGrid({
  photos,
  selectMode = false,
  onToggleSelect,
  onDelete,
  emptyHint,
}: {
  photos: UploadedTilePhoto[];
  selectMode?: boolean;
  onToggleSelect?: (id: string) => void;
  onDelete?: (id: string) => void;
  emptyHint?: string;
}) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 0) setWidth(w);
    });
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  const gap = 8;
  const minTile = 96; // ≈ the old 6rem minmax floor
  const columns = Math.max(1, Math.floor((width + gap) / (minTile + gap)));
  const count = photos.length;
  const rowCount = Math.ceil(count / columns);
  const tileW = columns > 0 ? (width - (columns - 1) * gap) / columns : minTile;
  const rowHeight = Math.max(1, Math.round(tileW + gap));

  // Cap the scroll viewport so very large batches stay in a fixed ~60vh window.
  // Small batches shrink to fit.
  const maxViewport = Math.round(window.innerHeight * 0.6);
  const fullHeight = rowCount * rowHeight;
  const viewport = Math.min(maxViewport, Math.max(rowHeight, fullHeight));

  const rowProps = useMemo<TileRowProps>(
    () => ({ columns, count, gap, photos, selectMode, onToggleSelect, onDelete }),
    [columns, count, gap, photos, selectMode, onToggleSelect, onDelete],
  );

  if (count === 0) {
    return emptyHint ? (
      <div className="py-6 text-center text-xs" style={{ color: '#6B7280' }}>{emptyHint}</div>
    ) : null;
  }

  return (
    <div ref={wrapRef} style={{ width: '100%' }}>
      {/* Shimmer keyframes for the loading placeholder (FIX #5). Scoped here so
          the grid is self-contained — no global CSS dependency. */}
      <style>{`
        @keyframes tileShimmer { 0% { background-position: -150% 0; } 100% { background-position: 150% 0; } }
        .tile-shimmer {
          background-image: linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.65) 50%, rgba(255,255,255,0) 100%);
          background-size: 200% 100%;
          animation: tileShimmer 1.4s ease-in-out infinite;
        }
      `}</style>
      {width > 0 && (
        <VirtualList
          rowComponent={TileRow}
          rowCount={rowCount}
          rowHeight={rowHeight}
          rowProps={rowProps}
          overscanCount={2}
          style={{ height: viewport, width: '100%' }}
        />
      )}
    </div>
  );
}

export default UploadedTileGrid;
